import { useEffect, useRef } from "react";
import { useImmer } from "use-immer";
import { toast } from "react-toastify";

import { WORKFLOW_IDS } from "@intelligems/ig-plugin/utils";
import type {
  AgenticLogicSurface,
  AiNodeContext,
  StartWorkflowResponseBodyDto,
} from "@intelligems/ig-types";

import { agenticLogicRuleIdeasContextKey } from "@src/store/slices/appSettings";

export interface AgenticLogicRuleIdeasWorkflowVariables {
  ruleIdeas: {
    ideas: string[];
  };
}

const RULE_IDEAS_ERROR = "Atlas could not generate new rule ideas. Try again.";

interface RunRuleIdeasWorkflowParams {
  workflowId: string;
  version: string;
  initialContext: Partial<AiNodeContext>;
}

type RunRuleIdeasWorkflow = (
  params: RunRuleIdeasWorkflowParams
) => Promise<StartWorkflowResponseBodyDto<AgenticLogicRuleIdeasWorkflowVariables> | null>;

interface AgenticLogicRuleIdeasPersistence {
  initialIdeas: readonly string[];
  onIdeasChange: (ideas: string[]) => void;
  /**
   * The surface the ideas must be written for, when the creation view was
   * entered from a placement that already knows where it renders. Undefined on
   * the rules list, which is bound to no placement and where every surface is
   * still open.
   *
   * Part of the request AND of what makes a set stale: switching entries is a
   * different context, so the ideas are generated again rather than reused
   * from the previous one.
   */
  placementSurface?: AgenticLogicSurface;
}

export const useAgenticLogicRuleIdeas = (
  runWorkflow: RunRuleIdeasWorkflow,
  {
    initialIdeas,
    onIdeasChange,
    placementSurface,
  }: AgenticLogicRuleIdeasPersistence
) => {
  const [ideas, setIdeas] = useImmer<string[]>(
    () => validRuleIdeas(initialIdeas) ?? []
  );
  // Which context the ideas on screen were settled for. A context is settled
  // once — by adopting a usable persisted set or by one workflow run — so
  // re-entering the same placement costs nothing and entering a different one
  // never shows the previous placement's ideas.
  const settledContext = useRef<string | null>(null);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    const context = agenticLogicRuleIdeasContextKey(placementSurface);
    if (settledContext.current !== context) {
      settledContext.current = context;
      const persistedIdeas = validRuleIdeas(initialIdeas);
      if (persistedIdeas) {
        setIdeas(persistedIdeas);
      } else {
        // What this org has already been shown IN THIS CONTEXT, valid as a
        // whole set or not: a persisted set that failed validation still tells
        // the model which territory it covered, so the regeneration does not
        // hand back the same three ideas in a well-formed wrapper.
        void generateRuleIdeas(
          runWorkflow,
          nonEmptyStrings(initialIdeas),
          placementSurface
        ).then((nextIdeas) => {
          if (mounted.current && nextIdeas) {
            setIdeas(nextIdeas);
            onIdeasChange(nextIdeas);
          }
        });
      }
    }

    return () => {
      mounted.current = false;
    };
  }, [initialIdeas, onIdeasChange, placementSurface, runWorkflow, setIdeas]);

  return { ideas };
};

const generateRuleIdeas = async (
  runWorkflow: RunRuleIdeasWorkflow,
  ideasToAvoid: string[],
  placementSurface?: AgenticLogicSurface
) => {
  const response = await runWorkflow({
    workflowId: WORKFLOW_IDS.AGENTIC_LOGIC_RULE_IDEAS,
    version: "1.0.0",
    initialContext: {
      // `placementSurface` is omitted rather than sent as null when the entry
      // is bound to no placement: the prompt branches on it with mustache's
      // section idiom, and only an absent (or falsy) variable takes the "every
      // surface is open" branch.
      variables: placementSurface
        ? { ideasToAvoid, placementSurface }
        : { ideasToAvoid },
    },
  });
  const nextIdeas = validRuleIdeas(response?.variables?.ruleIdeas?.ideas);
  if (!nextIdeas) {
    toast.error(RULE_IDEAS_ERROR);
    return null;
  }

  return nextIdeas;
};

const validRuleIdeas = (candidateIdeas: unknown) => {
  if (
    !Array.isArray(candidateIdeas) ||
    candidateIdeas.length !== 3 ||
    !candidateIdeas.every((idea): idea is string => typeof idea === "string")
  ) {
    return null;
  }

  const normalizedIdeas = candidateIdeas.map((idea) => idea.trim());
  if (
    normalizedIdeas.some((idea) => idea.length === 0 || idea.length > 100) ||
    new Set(normalizedIdeas).size !== normalizedIdeas.length
  ) {
    return null;
  }

  return normalizedIdeas;
};

/** The readable ideas in a persisted set, valid as a whole set or not. */
const nonEmptyStrings = (candidateIdeas: readonly unknown[]): string[] =>
  candidateIdeas
    .filter((idea): idea is string => typeof idea === "string")
    .map((idea) => idea.trim())
    .filter((idea) => idea.length > 0);
